"use client";

import React from "react";
import { Feature } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Image from "next/image";
import Link from "next/link";
import { renderRichText } from "@/lib/richText";
import VideoPlayer from "@/components/VideoPlayer";

interface FeatureProps {
  blok?: Feature & SbBlokData;
}

const FeatureComponent: React.FC<FeatureProps> = ({ blok }) => {
  if (!blok) return null;

  const {
    bg_style,
    intro_title,
    title,
    description,
    content_type,
    image,
    image_width,
    image_height,
    video_details,
    image_position = "left",
    cta_button,
    cta_text,
    cta_link,
    icon_list,
    use_bottom_line,
  } = blok;

  const videoDetail = video_details?.[0];

  const renderMedia = () => {
    if (content_type === "video" && videoDetail && videoDetail.tag) {
      return (
        <VideoPlayer videoId={videoDetail.tag} />
      );
    } else if (image?.filename) {
      return (
        <>
          <Image
            src={image.filename}
            alt={image.alt || `${title} Avatar`}
            title={`${title} Avatar`}
            className={!image_width || !image_height ? "img-cover h-100 w-100" : undefined}
            width={Number(image_width) || 100}
            height={Number(image_height) || 100}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            quality={75}
          />
        </>
      );
    }
    return null;
  };

  const getListClass = () => {
    switch (blok.bullet_style) {
      case "target":
        return "feature_bullet_target";
      case "checkmark":
        return "feature_bullet_checkmark";
      default:
        return "list-checked";
    }
  };

  const contentSection = (
    <>
      {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
      {title && <h2 id={title.replace(/\s+/g, "-").toLowerCase()}
                    className={blok?.title_color ? `text-${blok.title_color}` : undefined}>{title}</h2>}
      {description && (
        <div
          className={`feature-description ${getListClass()}`}
          dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
        />
      )}
      {cta_button && cta_text && cta_link?.url && (
        <Link
          href={cta_link.url}
          target={(cta_link.linktype as string) === "external" ? "_blank" : "_self"}
          rel={(cta_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
          className="btn btn-primary mt-3"
        >
          {cta_text}
        </Link>
      )}
    </>
  );


  if (content_type === "icons") {
    return (
      <>
        <div {...storyblokEditable(blok)} className="container text-center text-md-start py-4 py-md-5 feature_icons">
          <div className="row align-items-md-center justify-content-xl-center text-center text-md-start gy-3 gy-md-0">
            <div className="col-md col-xxl-20 text-center">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && <h2 className={blok?.title_color ? `text-${blok.title_color}` : undefined}
                            id={title.replace(/\s+/g, "-").toLowerCase()}>{title}</h2>}
              {description && (
                <div
                  dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                />
              )}
              {icon_list && icon_list.length > 0 && (
                icon_list.some(icon => icon.title) ? (
                  <div className="row row-cols-1 row-cols-md-2 row-cols-xl-4 gy-4 gx-md-4 text-center mt-3">
                    {icon_list.map((icon, index) => (
                      <div className="col" key={icon.id || index}>
                        {icon.filename && (
                          <Image
                            src={icon.filename}
                            alt={icon.alt || ""}
                            title={icon.name || ""}
                            className="mb-3 js-lazy-load"
                            data-src={icon.filename}
                            width="70"
                            height="70"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            loading="lazy"
                            quality={75}
                          />
                        )}
                        {icon.name && <h3>{icon.name}</h3>}
                        {icon.title && <p>{icon.title}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="row justify-content-center row-cols-1 row-cols-md-2 row-cols-xl-5 gx-4 gy-4 text-center mt-3">
                    {icon_list.map((icon, index) => (
                      <div className="col" key={icon.id || index}>
                        <div className="card card-penetration-testing-hover rounded-3 shadow border-0 p-3 text-center">
                          {icon.filename && (
                            <Image
                              src={icon.filename}
                              alt={icon.alt || icon.name || ""}
                              width={70}
                              height={70}
                              className="mx-auto d-inline-block mb-2 mb-lg-3"
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                              loading="lazy"
                              quality={75}
                            />
                          )}
                          <p className="h4 mb-0">{icon.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }


  if (image_position === "full") {
    return (
      <>
        <section className="feature_full container py-4 py-md-5" {...(blok && storyblokEditable(blok))}>
          {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
          {title && <h2 className={`text-center ${blok?.title_color ? `text-${blok.title_color}` : ""}`}
                        id={title.replace(/\s+/g, "-").toLowerCase()}>{title}</h2>}
          <div>
            {description && (
              <div
                className="text-center"
                dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
              />
            )}
            {cta_button && cta_text && cta_link?.url && (
              <div className="text-center">
                <Link
                  href={cta_link.url}
                  target={(cta_link.linktype as string) === "external" ? "_blank" : "_self"}
                  rel={(cta_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
                  className="btn btn-lg btn-primary js-cta-btn"
                >
                  {cta_text}
                </Link>
              </div>
            )}
          </div>
        </section>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "full_left") {
    return (
      <>
        <section className="feature_full_left container py-4 py-lg-5" {...(blok && storyblokEditable(blok))}>
          <div>
            {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
            {title && <h2 className={`${blok?.title_color ? `text-${blok.title_color}` : ""}`}
                          id={title.replace(/\s+/g, "-").toLowerCase()}>{title}</h2>}
            {description && (
              <div
                dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
              />
            )}
            {cta_button && cta_text && cta_link?.url && (
              <Link
                href={cta_link.url}
                target={(cta_link.linktype as string) === "external" ? "_blank" : "_self"}
                rel={(cta_link.linktype as string) === "external" ? "noopener noreferrer" : ""}
                className="btn btn-lg btn-primary js-cta-btn"
              >
                {cta_text}
              </Link>
            )}
          </div>
        </section>
        {use_bottom_line && <hr />}
      </>
    );
  }


  if (image_position === "center_target") {
    return (
      <>
        <div className="feature_center_target container py-4 py-md-5" {...storyblokEditable(blok)}>
          <div className="row justify-content-center gy-3 gy-lg-0">
            <div className="col-24 col-md-18">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && <h2 className={`${blok?.title_color ? `text-${blok.title_color}` : ""}`}
                            id={title.replace(/\s+/g, "-").toLowerCase()}>{title}</h2>}
              {description && (
                <div
                  className="lead mb-4"
                  dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                />
              )}
              {icon_list && icon_list.length > 0 && (
                <ul className="lead text-start list-two-columns list-targets">
                  {icon_list.map((item, index) => (
                    <li key={item.id || index}>
                      {item.title && (
                        item.filename ? (
                          <Link
                            href={item.filename}
                            className="text-underlined text-secondary"
                            target="_blank"
                          >
                            {item.title}
                          </Link>
                        ) : (
                          item.title
                        )
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "report_left") {
    return (
      <>
        <div className="feature_report bg-light component-image-content">
          <div className="container row g-md-3 g-lg-3 align-items-center py-4 py-md-5 mx-auto">
            <div className="col-24 col-xl-10 mb-4 mb-xl-0 text-center">
              {renderMedia()}
              {cta_button && cta_link?.url && (
                <p>
                  <Link
                    href={cta_link.url}
                    className="btn btn-primary btn-lg mt-3"
                  >
                    Download now
                  </Link>
                </p>
              )}
            </div>
            <div className="col-24 col-xl-13 lead offset-xl-1">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && <h2 className={`${blok?.title_color ? `text-${blok.title_color}` : ""}`}
                            id={title.replace(/\s+/g, "-").toLowerCase()}>{title}</h2>}
              {description && (
                <div
                  className="mb-3"
                  dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                />
              )}
              {icon_list && icon_list.length > 0 && (
                <ul className="list-targets mb-0">
                  {icon_list.map((item, index) => (
                    <li key={item.id || index}>
                      {item.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "report_right") {
    return (
      <>
        <div className="feature_report container py-4 py-md-5" {...storyblokEditable(blok)}>
          <div className="row">
            <div className="col-md-12 col-lg-13 col-xl-14 col-xxl-16">
              {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
              {title && <h2 className={`${blok?.title_color ? `text-${blok.title_color}` : ""}`}>{title}</h2>}
              {description && (
                <div
                  className="feature-description"
                  dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                />
              )}
              {icon_list && icon_list.length > 0 && (
                <ul className="list-targets">
                  {icon_list.map((item, index) => (
                    <li key={item.id || index}>
                      {item.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="col text-center">
              {renderMedia()}
              {cta_button && cta_link?.url && (
                <p>
                  <Link
                    href={cta_link.url}
                    className="btn btn-primary btn-lg mt-3"
                  >
                    Download now
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "certified_left") {
    return (
      <>
        <div {...storyblokEditable(blok)} className="container feature_certified_left">
          <div className="py-4">
            <div className="two-thirds-centered">
              <div className="card-blog-certified">
                <div className="aside">
                  {image?.filename && (
                    <Image
                      src={image.filename}
                      alt={image.alt || title || ""}
                      width={241}
                      height={310}
                      className="img-fluid"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  )}
                </div>
                <div className="content">
                  {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
                  {title && <h3 className={`${blok?.title_color ? `text-${blok.title_color}` : ""}`}>{title}</h3>}
                  {description && (
                    <div
                      dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }

  if (image_position === "certified_right") {
    return (
      <>
        <div {...storyblokEditable(blok)} className="container feature_certified_right">
          <div className="py-4">
            <div className="two-thirds-centered">
              <div className="card-blog-certified card-blog-certified-alt">
                <div className="content">
                  {intro_title && <h5 className="text-secondary mb-2">{intro_title}</h5>}
                  {title && <h3 className={`${blok?.title_color ? `text-${blok.title_color}` : ""}`}>{title}</h3>}
                  {description && (
                    <div
                      dangerouslySetInnerHTML={{ __html: renderRichText(description) }}
                    />
                  )}
                </div>
                <div className="aside">
                  {image?.filename && (
                    <Image
                      src={image.filename}
                      alt={image.alt || title || ""}
                      width={241}
                      height={310}
                      className="img-fluid"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {use_bottom_line && <hr />}
      </>
    );
  }


  return (
    <>
      {bg_style === "dark" ? (
        <>
          <div className="bg-light feature_content" {...(blok && storyblokEditable(blok))}>
            <div>
              {image_position === "center" ? (
                <div
                  className="row align-items-md-center">
                  <div className="col-md col-lg-12 offset-xl-1 col-xl-13 col-xxl-12">
                    {renderMedia()}
                  </div>
                  <div className="col-md col-lg-12 offset-xl-1 col-xl-13 col-xxl-12">
                    {contentSection}
                  </div>
                </div>
              ) : (
                image_position === "right" ? (
                  <div className="container row mx-auto align-items-center py-4 py-md-5">
                    <div className="col-24 col-xl-13 lead ">{contentSection}</div>
                    <div
                      className="col-24 col-xl-10 mb-4 mb-xl-0 text-center order-xl-last offset-xl-1">{renderMedia()}</div>
                  </div>
                ) : (
                  <div className="container row mx-auto align-items-center py-4 py-md-5">
                    <div className="col-24 col-xl-10 mb-4 mb-xl-0 text-center">{renderMedia()}</div>
                    <div className="col-24 col-xl-13 lead offset-xl-1">{contentSection}</div>
                  </div>
                )
              )}
            </div>
          </div>
          {use_bottom_line && <hr />}
        </>
      ) : (
        <>
          <div
            {...(blok && storyblokEditable(blok))}
            className="container feature_content"
          >
            {image_position === "center" ? (
              <div
                className="row align-items-md-center justify-content-xl-center text-center text-md-start gy-3 gy-md-0">
                <div className="col-md col-lg-12 offset-xl-1 col-xl-13 col-xxl-12">
                  {renderMedia()}
                </div>
                <div className="col-md col-lg-12 offset-xl-1 col-xl-13 col-xxl-12">
                  {contentSection}
                </div>
              </div>
            ) : (

              image_position === "right" ? (
                <div className="container row mx-auto align-items-center py-4 py-md-5">
                  <div className="col-24 col-xl-13 lead ">{contentSection}</div>
                  <div
                    className="col-24 col-xl-10 mb-4 mb-xl-0 text-center order-xl-last offset-xl-1">{renderMedia()}</div>
                </div>
              ) : (
                <div className="container row mx-auto align-items-center py-4 py-md-5">
                  <div className="col-24 col-xl-10 mb-4 mb-xl-0 text-center">{renderMedia()}</div>
                  <div className="col-24 col-xl-13 lead offset-xl-1">{contentSection}</div>
                </div>
              )

            )}
          </div>
          {use_bottom_line && <hr />}
        </>
      )}
    </>
  );
};

export default FeatureComponent;